import { useState, useEffect, useCallback } from 'react';
import { adminAPI, usersAPI } from '../../utils/api';
import { format } from 'date-fns';
import { ALL_ROLES, ROLE_COLOR } from '../../utils/rolesConfig';
import { Eye, EyeOff, Key, Lock, Plus, Search, SquarePen, Unlock, X } from 'lucide-react';

import { useToast, apiError, useConfirm } from '../../components/Toast';
import { useSearchParams } from 'react-router-dom';

// ── Inline icons ──────────────────────────────────────────────────────────
const PenIcon = () => <SquarePen size={13} />;
const PlusIcon = () => <Plus size={14} />;
const SearchIcon = () => <Search size={14} />;
const XIcon = () => <X size={14} />;

// Permission level helper
const permLevel = (role) => {
  if (['Administrator'].includes(role))       return { label:'System Admin Level', color:'#2E539F' };
  if (['Admin'].includes(role))               return { label:'Admin Level',       color:'#2E539F' };
  if (['CEO','CPO','Chief Operating Officer'].includes(role)) return { label:'Executive Level', color:'#8b5cf6' };
  if (['Manager','Chief Operating Officer','Head of Product Delivery'].includes(role)) return { label:'Manager Level', color:'#0891b2' };
  if (['Developer','Senior Developer'].includes(role)) return { label:'Developer Level', color:'#2E539F' };
  return { label:'Specialist Level', color:'#10b981' };
};

const PERM_LEVELS = ['SystemAdmin','Admin','Executive','Manager','Specialist','Developer'];

// ── Default form state ─────────────────────────────────────────────────────
const DEFAULT_FORM = { name:'', email:'', role:'', password:'' };

// ── Modal wrapper ──────────────────────────────────────────────────────────
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}/>
      <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-lg z-10"
        style={{ border:'1px solid var(--border)' }}>
        <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor:'var(--border)' }}>
          <h3 className="font-bold text-base" style={{ color:'var(--text)' }}>{title}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--surface-alt)] transition-colors" style={{ color:'var(--text-secondary)' }}><XIcon/></button>
        </div>
        <div className="p-6">
          {/* Dummy fields off-screen to absorb browser autofill */}
          <div aria-hidden="true" style={{position:'absolute',left:'-9999px',top:'-9999px',opacity:0,height:0,overflow:'hidden'}}>
            <input type="text" name="fakeusernameremembered" autoComplete="username" tabIndex={-1}/>
            <input type="password" name="fakepasswordremembered" autoComplete="current-password" tabIndex={-1}/>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────
export default function AdminUsersPage() {
  const confirm = useConfirm();
  const toast = useToast();
  const [users,       setUsers]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [search,      setSearch]      = useState('');
  const [roleFilter,  setRoleFilter]  = useState('');
  const [levelFilter, setLevelFilter] = useState('');

  // Create/Edit modal
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editUser,    setEditUser]    = useState(null);
  const [form,        setForm]        = useState({ ...DEFAULT_FORM });
  const [formError,   setFormError]   = useState('');
  const [saving,      setSaving]      = useState(false);

  // Password reset modal
  const [resetUser,   setResetUser]   = useState(null);
  const [newPw,       setNewPw]       = useState('');
  const [confirmPw,   setConfirmPw]   = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);

  // ── Load users ────────────────────────────────────────────────────────────


  /**
   * ?filter= comes from the admin dashboard's attention cards. Without it the
   * link landed on the full user list, which looks like it worked.
   */
  const [sp, setSp] = useSearchParams();
  const attention = sp.get('filter') || '';
  const ATTENTION_LABEL = { deactivated:'Deactivated', never:'Never signed in', dormant:'Dormant with open work' };
  const clearAttention = () => { const n = new URLSearchParams(sp); n.delete('filter'); setSp(n); };
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await adminAPI.getUsers({ search: search || undefined, role: roleFilter || undefined,
                                          filter: attention || undefined });
      setUsers(r.data || []);
    } catch(e) {
      setError(e.response?.data?.message || 'Failed to load users. Check server connection.');
    } finally { setLoading(false); }
  }, [search, roleFilter, attention]);

  useEffect(() => { load(); }, [load]);

  // ── Derived: apply level filter client-side ────────────────────────────────
  const filtered = levelFilter
    ? users.filter(u => permLevel(u.role).label === levelFilter + ' Level')
    : users;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditUser(null);
    setForm({ ...DEFAULT_FORM });
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ name:u.name, email:u.email, role:u.role, password:'' });
    setFormError('');
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return setFormError('Full name is required');
    if (!form.email.trim()) return setFormError('Email is required');
    if (!form.role) return setFormError('Please select a system role');
    setSaving(true); setFormError('');
    try {
      // No password. POST /auth/register creates the account with an
      // unguessable one and emails a link so the person sets their own, which
      // means an administrator never chooses or sees anybody's password.
      const payload = { name:form.name.trim(), email:form.email.trim(), role:form.role };
      editUser
        ? await usersAPI.update(editUser.id, payload)
        : await usersAPI.create(payload);
      toast.success(editUser ? 'User updated' : `Invitation sent to ${form.email.trim()}`);
      setModalOpen(false);
      load();
    } catch(e) {
      setFormError(e.response?.data?.message || 'Failed to save user');
    } finally { setSaving(false); }
  };

  const handleToggleActive = async (u) => {
    const action = u.is_active ? 'deactivate' : 'reactivate';
    if (!await confirm(`${action.charAt(0).toUpperCase()+action.slice(1)} ${u.name}?\n\n${u.is_active ? 'They will not be able to log in.' : 'They will be able to log in again.'}`,
      { danger:!!u.is_active, title:`${action.charAt(0).toUpperCase()+action.slice(1)} account`, confirmLabel:action.charAt(0).toUpperCase()+action.slice(1) })) return;
    try { await usersAPI.update(u.id, { is_active: !u.is_active });
    toast.success('User updated'); load(); }
    catch(e) { toast.error(apiError(e, `Failed to ${action} user`)); }
  };

  const handleLock = async (u) => {
    if (!await confirm(`Lock ${u.name}'s account?\n\nThey will be unable to log in until unlocked.`)) return;
    try { await usersAPI.lock(u.id); load(); }
    catch(e) { toast.error(apiError(e, 'Failed to lock account')); }
  };

  const handleUnlock = async (u) => {
    try { await usersAPI.unlock(u.id); load(); }
    catch(e) { toast.error(apiError(e, 'Failed to unlock account')); }
  };

  const openReset = (u) => { setResetUser(u); setNewPw(''); setConfirmPw(''); setShowResetPw(false); };

  const handleAdminReset = async () => {
    if (newPw.length < 8) return toast.warning('Password must be at least 8 characters');
    if (newPw !== confirmPw) return toast.warning('Passwords do not match');
    setResetSaving(true);
    try {
      await usersAPI.adminResetPw(resetUser.id, newPw);
      setResetUser(null);
      toast.warning(`Password for ${resetUser.name} has been reset successfully. Notify them securely.`);
    } catch(e) { toast.error(apiError(e, 'Failed to reset password')); }
    finally { setResetSaving(false); }
  };

  // ── Grouped roles for dropdown ─────────────────────────────────────────
  const rolesByLevel = {
    Admin:      ALL_ROLES.filter(r => r.permLevel === 'SystemAdmin' || r.permLevel === 'Admin'),
    Executive:  ALL_ROLES.filter(r => r.permLevel === 'Executive'),
    Manager:    ALL_ROLES.filter(r => r.permLevel === 'Manager'),
    Specialist: ALL_ROLES.filter(r => r.permLevel === 'Specialist'),
    Developer:  ALL_ROLES.filter(r => r.permLevel === 'Developer'),
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="text-sm mt-0.5" style={{ color:'var(--text-secondary)' }}>
            Manage team members, roles, and account access
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm">
          <PlusIcon/> Add User
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48 max-w-xs">
          <span className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}><SearchIcon/></span>
          <input className="input pl-9 text-sm" placeholder="Search users..."
            value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <select className="select text-sm w-48" value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All Roles</option>
          {ALL_ROLES.filter(r => r.value !== 'Specialist').map(r =>
            <option key={r.value} value={r.value}>{r.label}</option>
          )}
        </select>
        <select className="select text-sm w-44" value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
          <option value="">All Levels</option>
          {PERM_LEVELS.map(l =>
            <option key={l} value={l}>{l} Level</option>
          )}
        </select>
        {(search || roleFilter || levelFilter) && (
          <button onClick={() => { setSearch(''); setRoleFilter(''); setLevelFilter(''); }}
            className="text-xs px-3 py-1.5 rounded-lg border transition-colors hover:bg-[var(--surface-alt)]"
            style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Error state */}
      {error && (
        <div className="p-4 rounded-xl text-sm" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
          ⚠ {error}
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label:'Total Users',  val: users.length,                         color:'#2E539F' },
          { label:'Active',       val: users.filter(u=>u.is_active).length,  color:'#10b981' },
          { label:'Inactive',     val: users.filter(u=>!u.is_active).length, color:'#ef4444' },
          { label:'Never Logged', val: users.filter(u=>!u.last_login).length,color:'#f97316' },
        ].map(s => (
          <div key={s.label} className="card py-3 px-4">
            <p className="text-xs font-medium uppercase tracking-wider" style={{ color:'var(--text-secondary)' }}>{s.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color:s.color }}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Users table */}
      <div className="card overflow-hidden p-0">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-3xl mb-2">👥</p>
            <p className="font-semibold" style={{ color:'var(--text)' }}>No users found</p>
            <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>Try adjusting your filters or add a new user</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ background:'var(--surface-alt)', borderColor:'var(--border)' }}>
                  {['User','Email','Role / Level','Last Login','Status','Actions'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                      style={{ color:'var(--text-secondary)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((u, i) => {
                  const perm = permLevel(u.role);
                  return (
                    <tr key={u.id}
                      className={`border-b transition-colors hover:bg-[var(--surface-alt)] ${!u.is_active ? 'opacity-60' : ''}`}
                      style={{ borderColor:'var(--border)', background: i%2===1 ? 'rgba(0,0,0,.015)' : 'transparent' }}>

                      {/* User */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-white text-xs flex-shrink-0"
                            style={{ background: u.is_active ? (u.avatar_color||'#2E539F') : '#94a3b8' }}>
                            {u.name?.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium leading-tight" style={{ color:'var(--text)' }}>{u.name}</p>
                          </div>
                        </div>
                      </td>

                      {/* Email */}
                      <td className="px-4 py-3 text-xs" style={{ color:'var(--text-secondary)' }}>{u.email}</td>

                      {/* Role / Level */}
                      <td className="px-4 py-3">
                        <span className={`badge text-xs ${ROLE_COLOR[u.role]||''}`}>{u.role}</span>
                        <p className="text-xs mt-0.5 font-medium" style={{ color: perm.color }}>{perm.label}</p>
                      </td>

                      {/* Last Login */}
                      <td className="px-4 py-3 text-xs whitespace-nowrap" style={{ color:'var(--text-secondary)' }}>
                        {u.last_login
                          ? format(new Date(u.last_login), 'dd MMM yyyy')
                          : <span style={{ color:'#f97316' }}>Never logged in</span>}
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <span className={`badge text-xs ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap">
                          {/* Edit */}
                          <button onClick={() => openEdit(u)} title="Edit user"
                            className="p-1.5 rounded-lg hover:bg-[var(--surface-alt)] transition-colors"
                            style={{ color:'var(--text-secondary)' }}>
                            <PenIcon/>
                          </button>

                          {/* Reset Password */}
                          <button onClick={() => openReset(u)} title="Reset password"
                            className="p-1.5 rounded-lg hover:bg-amber-50 transition-colors text-amber-500">
                            <Key size={13}/>
                          </button>

                          {/* Lock / Unlock */}
                          {u.is_active ? (
                            <button onClick={() => handleLock(u)} title="Lock account"
                              className="p-1.5 rounded-lg hover:bg-red-50 transition-colors text-red-400">
                              <Lock size={13}/>
                            </button>
                          ) : (
                            <button onClick={() => handleUnlock(u)} title="Unlock account"
                              className="p-1.5 rounded-lg hover:bg-emerald-50 transition-colors text-emerald-500">
                              <Unlock size={13}/>
                            </button>
                          )}

                          {/* Activate / Deactivate */}
                          <button onClick={() => handleToggleActive(u)}
                            title={u.is_active ? 'Deactivate user' : 'Reactivate user'}
                            className={`text-xs px-2 py-1 rounded-lg font-medium transition-colors ${
                              u.is_active
                                ? 'text-red-400 hover:bg-red-50'
                                : 'text-emerald-500 hover:bg-emerald-50'
                            }`}>
                            {u.is_active ? 'Deactivate' : 'Reactivate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2.5 border-t text-xs" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
          Showing {filtered.length} of {users.length} users
          {levelFilter || roleFilter ? ` · filtered` : ''}
        </div>
      </div>

      {/* ── Create / Edit User Modal ── */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editUser ? `Edit — ${editUser.name}` : 'Add New User'} key={`modal-${editUser?.id || 'new'}-${modalOpen}`}>
        <div className="space-y-4">
          {formError && (
            <div className="p-3 rounded-xl text-sm" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
              {formError}
            </div>
          )}

          <div>
            <label className="label">Full Name <span className="text-red-500">*</span></label>
            <input className="input" value={form.name} autoComplete="off" data-lpignore="true" data-form-type="other" name="new-user-fullname"
              onChange={e => setForm(f => ({...f, name:e.target.value}))}
              placeholder="Jane Smith" autoFocus/>
          </div>

          <div>
            <label className="label">Email Address <span className="text-red-500">*</span></label>
            <input type="text" className="input" value={form.email} autoComplete="off" data-lpignore="true" data-form-type="other" name="new-user-email"
              onChange={e => setForm(f => ({...f, email:e.target.value}))}
              placeholder="jane@company.com"/>
          </div>

<div>
            <label className="label">System Role <span className="text-red-500">*</span> <span className="text-xs font-normal" style={{ color:'var(--text-secondary)' }}>(controls permissions &amp; access)</span></label>
            <select className="select" value={form.role}
              onChange={e => setForm(f => ({...f, role:e.target.value}))}>
              <option value="" disabled>— Select a role —</option>
              {Object.entries(rolesByLevel).map(([level, roles]) => (
                <optgroup key={level} label={`── ${level} Level ──`}>
                  {roles.filter(r => r.value !== 'Specialist').map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {form.role && (
              <p className="text-xs mt-1 font-medium" style={{ color: permLevel(form.role).color }}>
                → {permLevel(form.role).label}
              </p>
            )}
          </div>

          {!editUser && (
            <p className="text-xs p-2.5 rounded-lg" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>
              The user receives an email with a link to set their own password. The link expires in 3 days.
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t" style={{ borderColor:'var(--border)' }}>
            <button onClick={() => setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2">
              {saving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              {editUser ? 'Save Changes' : 'Create & Send Invite'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Admin Password Reset Modal ── */}
      {resetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setResetUser(null)}/>
          <div className="relative bg-[var(--surface)] rounded-2xl shadow-2xl w-full max-w-sm z-10"
            style={{ border:'1px solid var(--border)' }}>
            <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor:'var(--border)' }}>
              <div>
                <h3 className="font-bold" style={{ color:'var(--text)' }}>Reset Password</h3>
                <p className="text-xs mt-0.5" style={{ color:'var(--text-secondary)' }}>
                  Setting new password for <strong>{resetUser.name}</strong>
                </p>
              </div>
              <button onClick={() => setResetUser(null)} className="p-1 rounded hover:bg-[var(--surface-alt)]" style={{ color:'var(--text-secondary)' }}>
                <XIcon/>
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="label">New Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type={showResetPw?'text':'password'} className="input pr-10" autoComplete="new-password"
                    value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Enter new password" autoFocus/>
                  <button type="button" onClick={()=>setShowResetPw(p=>!p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color:'var(--text-secondary)' }}>
                    {showResetPw ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
                {newPw && (
                  <div className="mt-2 grid grid-cols-2 gap-1">
                    {[
                      {label:'8+ characters',     ok:newPw.length>=8},
                      {label:'Uppercase (A-Z)',    ok:/[A-Z]/.test(newPw)},
                      {label:'Lowercase (a-z)',    ok:/[a-z]/.test(newPw)},
                      {label:'Number (0-9)',        ok:/[0-9]/.test(newPw)},
                      {label:'Special (!@#...)',   ok:/[^A-Za-z0-9]/.test(newPw)},
                    ].map(r=>(
                      <div key={r.label} className="flex items-center gap-1 text-xs">
                        <span style={{color:r.ok?'#10b981':'#94a3b8'}}>{r.ok?'✓':'○'}</span>
                        <span style={{color:r.ok?'#10b981':'var(--text-secondary)'}}>{r.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="label">Confirm Password <span className="text-red-500">*</span></label>
                <div className="relative">
                  <input type={showResetPw?'text':'password'} className="input pr-10" autoComplete="new-password"
                    value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repeat new password"/>
                  <button type="button" onClick={()=>setShowResetPw(p=>!p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2" style={{color:'var(--text-secondary)'}}>
                    {showResetPw ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </div>
              <div className="p-3 rounded-xl text-xs" style={{ background:'rgba(245,158,11,.08)', color:'#b45309' }}>
                ⚠ Notify the user of their new password securely. This action is recorded in the audit log.
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2" style={{ borderColor:'var(--border)' }}>
              <button onClick={() => setResetUser(null)} className="btn-secondary">Cancel</button>
              <button onClick={handleAdminReset} disabled={!newPw || !confirmPw || resetSaving}
                className="btn-primary flex items-center gap-2">
                {resetSaving && <span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
